import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/** Size each chapter to its live content while an inert outgoing snapshot fades away. */
export function ChapterTransition({ transitionKey, snapshot, children }: { transitionKey: string; snapshot: ReactNode; children: ReactNode }) {
  const previous = useRef({ key: transitionKey, snapshot });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const liveRef = useRef<HTMLDivElement>(null);
  const [outgoing, setOutgoing] = useState<ReactNode>(null);
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  useLayoutEffect(() => {
    const prior = previous.current;
    previous.current = { key: transitionKey, snapshot };
    if (prior.key === transitionKey) return;
    if (timer.current) clearTimeout(timer.current);
    setOutgoing(reduced ? null : prior.snapshot);
    if (!reduced) timer.current = setTimeout(() => { setOutgoing(null); }, 800);
  }, [transitionKey, snapshot, reduced]);

  useLayoutEffect(() => {
    const live = liveRef.current;
    if (!live) return undefined;
    const reserve = () => {
      live.parentElement?.style.setProperty('--chapter-body-height', `${live.getBoundingClientRect().height}px`);
    };
    reserve();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(reserve);
    observer?.observe(live);
    return () => observer?.disconnect();
  }, [transitionKey]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return <div className="conversation-chapter-swap" data-handoff={outgoing ? 'true' : undefined}>
    {outgoing ? <div className="conversation-chapter-outgoing" aria-hidden="true" inert>{outgoing}</div> : null}
    <div ref={liveRef}><div className="conversation-chapter-incoming" key={transitionKey}>{children}</div></div>
  </div>;
}
